
import { useState } from 'react';

interface HeroSectionProps {
  onSearch: (query: string) => void;
  isPremiumUser?: boolean;
}

export default function HeroSection({ onSearch, isPremiumUser = false }: HeroSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [floodAddress, setFloodAddress] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  const handleFloodSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPremiumUser) {
      // Redirect to pricing page for non-premium users
      window.location.href = '/pricing';
      return;
    }
    
    if (floodAddress.trim()) {
      // Open FEMA Flood Map Service Center in new tab
      const encodedAddress = encodeURIComponent(floodAddress.trim());
      window.open(`https://msc.fema.gov/portal/search?AddressQuery=${encodedAddress}#searchresultsanchor`, '_blank');
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 py-20">
      <div className="absolute inset-0 bg-black/20"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            County Assessor <span className="text-emerald-400">Directory</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            Find county assessor offices, appraisal districts, and access property records across all 50 states
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* County/Assessor Search */}
          <form onSubmit={handleSearch} className="relative">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-xl"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by county name, assessor, or service..."
                className="w-full pl-12 pr-32 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-lg"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Search
              </button>
            </div>
          </form>

          {/* FEMA Flood Data Search - Premium Only */}
          <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 ${!isPremiumUser ? 'relative' : ''}`}>
            {!isPremiumUser && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-amber-600 rounded-full mx-auto mb-4">
                    <i className="ri-vip-crown-line text-white text-2xl"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">Premium Feature</h4>
                  <p className="text-slate-300 text-sm mb-4">FEMA Flood Zone Lookup requires a premium subscription</p>
                  <button
                    onClick={() => window.location.href = '/pricing'}
                    className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Upgrade Now
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                <i className="ri-flood-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg flex items-center">
                  FEMA Flood Zone Lookup
                  {!isPremiumUser && (
                    <span className="ml-2 px-2 py-1 bg-amber-600 text-white text-xs rounded-full">PREMIUM</span>
                  )}
                </h3>
                <p className="text-slate-300 text-sm">Search flood zone data by property address</p>
              </div>
            </div>
            
            <form onSubmit={handleFloodSearch} className="relative">
              <div className="relative">
                <i className="ri-map-pin-line absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-xl"></i>
                <input
                  type="text"
                  value={floodAddress}
                  onChange={(e) => setFloodAddress(e.target.value)}
                  placeholder="Enter property address (e.g., 123 Main St, City, State)"
                  className="w-full pl-12 pr-40 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={!isPremiumUser}
                />
                <button
                  type="submit"
                  className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${isPremiumUser ? 'bg-blue-600 hover:bg-blue-500' : 'bg-slate-600 cursor-not-allowed'} text-white px-4 py-1.5 rounded-md font-semibold transition-colors whitespace-nowrap text-sm`}
                  disabled={!isPremiumUser}
                >
                  <i className="ri-external-link-line mr-1"></i>
                  Check Flood Zone
                </button>
              </div>
            </form>
            
            <div className="mt-3 text-xs text-slate-400">
              {isPremiumUser 
                ? "Links to official FEMA Flood Map Service Center for accurate flood zone determination"
                : "Premium feature - Links to official FEMA Flood Map Service Center"
              }
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="text-3xl font-bold text-emerald-400 mb-2">3,143</div>
            <div className="text-slate-300 text-sm">County Offices</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-emerald-400 mb-2">50</div>
            <div className="text-slate-300 text-sm">States Covered</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-400 mb-2">FEMA</div>
            <div className="text-slate-300 text-sm">
              Flood Data
              {!isPremiumUser && <span className="block text-xs text-amber-400">Premium</span>}
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-emerald-400 mb-2">24/7</div>
            <div className="text-slate-300 text-sm">Access</div>
          </div>
        </div>
      </div>
    </section>
  );
}
